// ═══════════════════════════════════════════════════════════════════════════════
// PICS Content — Multi-language (zh-Hant / zh-Hans / en)
// ═══════════════════════════════════════════════════════════════════════════════

import type { Language } from '@fdh/shared';

type ConsentSection = {
  title: string;
  content: string;
};

export type ConsentContent = {
  pageTitle: string;
  subtitle: string;
  versionLabel: string;
  sections: ConsentSection[];
  checkboxLabel: string;
  buttonLabel: string;
  loadingText: string;
  errorRetry: string;
  alreadyConsented: string;
  scrollHint: string;
  dataNoticeFooter: string;
};

export const consentContent: Partial<Record<Language, ConsentContent>> = {
  // ═══════════════════════════════════════════════════════════
  // Traditional Chinese
  // ═══════════════════════════════════════════════════════════
  'zh-Hant': {
    pageTitle: '個人資料收集聲明',
    subtitle: 'Personal Information Collection Statement (PICS)',
    versionLabel: '版本',
    sections: [
      {
        title: '1. 簡介',
        content:
          'FDH（「本應用程式」）是專為香港僱主與家務助理而設計的家務管理平台。本個人資料收集聲明（「PICS」）乃根據香港《個人資料（私隱）條例》（第 486 章）所發出。使用本應用程式即表示您已閱讀、理解並同意本聲明。',
      },
      {
        title: '2. 我們收集的資料',
        content:
          '我們收集並處理以下類別的個人資料：\n\n' +
          'a) 帳戶資料：電郵地址、暱稱、用戶角色（僱主／家務助理）及加密密碼\n' +
          'b) 任務驗證資料：照片（每次完成任務 1-3 張）、GPS 定位資料（可選，由用戶自行開啟）及備註\n' +
          'c) 財務資料：收據影像、逐項開支資料、已給的錢／收到的錢資料及結算記錄\n' +
          'd) 家庭成員資料：姓名、生日、食物偏好、食物敏感及健康狀況（由僱主輸入，並已確認取得家人同意）\n' +
          'e) 通訊資料：配對僱主與家務助理之間的留言及訊息\n' +
          'f) 技術資料：裝置資訊、IP 地址、瀏覽器類型及使用記錄',
      },
      {
        title: '3. 收集資料的目的',
        content:
          '您的個人資料用於以下目的：\n\n' +
          'a) 帳戶註冊、身份驗證及會話管理\n' +
          'b) 支援僱主與家務助理之間的任務指派、完成驗證及審核\n' +
          'c) 使用人工智能（AI）分析收據影像以追蹤開支\n' +
          'd) 僱主與家務助理之間的已給的錢／收到的錢管理及財務結算\n' +
          'e) 促進配對用戶之間的溝通\n' +
          'f) 遵守法律及監管要求\n' +
          'g) 系統安全、審計記錄及防止欺詐',
      },
      {
        title: '4. 資料保留及自動刪除',
        content:
          'a) 照片、GPS 資料及收據影像：上傳後 60 天自動刪除\n' +
          'b) AI 處理資料：AI 分析完成後立即從記憶體中刪除原始影像；僅保留結構化審核結果\n' +
          'c) 審計記錄：根據適用法規保留 3 年\n' +
          'd) 帳戶資料：在帳戶有效期間保留',
      },
      {
        title: '5. 人工智能（AI）處理',
        content:
          'a) 收據影像使用 Google Gemini AI 處理以提取結構化資料（公司、店舖、日期、貨幣、金額、項目）\n' +
          'b) AI 處理僅在記憶體中進行 — 原始影像不會被 AI 服務永久儲存\n' +
          'c) 用戶必須審核所有 AI 提取的資料，並可在提交前手動修改\n' +
          'd) AI 處理受每日使用限額限制',
      },
      {
        title: '6. 資料共享及披露',
        content:
          'a) 您的資料僅與您在應用程式內的配對僱主或家務助理共享\n' +
          'b) 收據影像會暫時由 Google Gemini AI 處理以提取資料\n' +
          'c) 我們不會向無關第三方出售、交易或共享您的個人資料\n' +
          'd) 在法律、法規或法律程序要求時，我們可能會披露資料',
      },
      {
        title: '7. 資料安全',
        content:
          'a) 所有資料均通過加密連接（HTTPS/TLS）傳輸\n' +
          'b) 密碼使用業界標準演算法進行雜湊處理\n' +
          'c) 身份驗證使用 JSON Web Tokens（JWT）配合 httpOnly 安全 Cookie\n' +
          'd) 資料存取僅限於授權的配對用戶',
      },
      {
        title: '8. 您的權利',
        content:
          '根據《個人資料（私隱）條例》，您有權：\n\n' +
          'a) 要求查閱您的個人資料\n' +
          'b) 要求更正不準確的個人資料\n' +
          'c) 要求了解所持有的資料類型及其使用方式\n' +
          'd) 撤回同意（可能導致無法使用本應用程式）',
      },
      {
        title: '9. 自願提供',
        content:
          '提供個人資料屬自願性質。然而，若您未能提供所需資料，我們可能無法向您提供本應用程式的服務。',
      },
      {
        title: '10. 聯絡我們',
        content:
          '如需查閱資料、更正要求或查詢有關我們的資料處理做法，請聯絡：\n\n' +
          '資料保護主任\n' +
          '電郵：maid-easy-privacy@hfsworld.com',
      },
    ],
    checkboxLabel: '我已閱讀、理解並同意以上個人資料收集聲明，並同意按照上述方式收集、處理及使用本人的個人資料。',
    buttonLabel: '同意並繼續',
    loadingText: '載入中...',
    errorRetry: '重試',
    alreadyConsented: '您已同意最新版本的個人資料收集聲明。',
    scrollHint: '請向下捲動閱讀完整聲明',
    dataNoticeFooter:
      '您的資料僅用於驗證及管理。照片及收據將在 60 天後自動刪除。',
  },

  // ═══════════════════════════════════════════════════════════
  // Simplified Chinese
  // ═══════════════════════════════════════════════════════════
  'zh-Hans': {
    pageTitle: '个人资料收集声明',
    subtitle: 'Personal Information Collection Statement (PICS)',
    versionLabel: '版本',
    sections: [
      {
        title: '1. 简介',
        content:
          'FDH（"本应用程序"）是专为香港僱主与家务助理而设计的家务管理平台。本个人资料收集声明（"PICS"）乃根据香港《个人资料（私隐）条例》（第 486 章）所发出。使用本应用程序即表示您已阅读、理解并同意本声明。',
      },
      {
        title: '2. 我们收集的资料',
        content:
          '我们收集并处理以下类别的个人资料：\n\n' +
          'a) 账户资料：电邮地址、昵称、用户角色（僱主／家务助理）及加密密码\n' +
          'b) 任务验证资料：照片（每次完成任务 1-3 张）、GPS 定位资料（可选，由用户自行开启）及备注\n' +
          'c) 财务资料：收据影像、逐项开支资料、已给的钱／收到的钱资料及结算记录\n' +
          'd) 家庭成员资料：姓名、生日、食物偏好、食物敏感及健康状况（由僱主输入，并已确认取得家人同意）\n' +
          'e) 通讯资料：配对僱主与家务助理之间的留言及信息\n' +
          'f) 技术资料：设备信息、IP 地址、浏览器类型及使用记录',
      },
      {
        title: '3. 收集资料的目的',
        content:
          '您的个人资料用于以下目的：\n\n' +
          'a) 账户注册、身份验证及会话管理\n' +
          'b) 支持僱主与家务助理之间的任务指派、完成验证及审核\n' +
          'c) 使用人工智能（AI）分析收据影像以追踪开支\n' +
          'd) 僱主与家务助理之间的已给的钱／收到的钱管理及财务结算\n' +
          'e) 促进配对用户之间的沟通\n' +
          'f) 遵守法律及监管要求\n' +
          'g) 系统安全、审计记录及防止欺诈',
      },
      {
        title: '4. 资料保留及自动删除',
        content:
          'a) 照片、GPS 资料及收据影像：上传后 60 天自动删除\n' +
          'b) AI 处理资料：AI 分析完成后立即从内存中删除原始影像；仅保留结构化审核结果\n' +
          'c) 审计记录：根据适用法规保留 3 年\n' +
          'd) 账户资料：在账户有效期间保留',
      },
      {
        title: '5. 人工智能（AI）处理',
        content:
          'a) 收据影像使用 Google Gemini AI 处理以提取结构化资料（公司、店铺、日期、货币、金额、项目）\n' +
          'b) AI 处理仅在内存中进行 — 原始影像不会被 AI 服务永久存储\n' +
          'c) 用户必须审核所有 AI 提取的资料，并可在提交前手动修改\n' +
          'd) AI 处理受每日使用限额限制',
      },
      {
        title: '6. 资料共享及披露',
        content:
          'a) 您的资料仅与您在应用程序内的配对僱主或家务助理共享\n' +
          'b) 收据影像会暂时由 Google Gemini AI 处理以提取资料\n' +
          'c) 我们不会向无关第三方出售、交易或共享您的个人资料\n' +
          'd) 在法律、法规或法律程序要求时，我们可能会披露资料',
      },
      {
        title: '7. 资料安全',
        content:
          'a) 所有资料均通过加密连接（HTTPS/TLS）传输\n' +
          'b) 密码使用业界标准算法进行哈希处理\n' +
          'c) 身份验证使用 JSON Web Tokens（JWT）配合 httpOnly 安全 Cookie\n' +
          'd) 资料访问仅限于授权的配对用户',
      },
      {
        title: '8. 您的权利',
        content:
          '根据《个人资料（私隐）条例》，您有权：\n\n' +
          'a) 要求查阅您的个人资料\n' +
          'b) 要求更正不准确的个人资料\n' +
          'c) 要求了解所持有的资料类型及其使用方式\n' +
          'd) 撤回同意（可能导致无法使用本应用程序）',
      },
      {
        title: '9. 自愿提供',
        content:
          '提供个人资料属自愿性质。然而，若您未能提供所需资料，我们可能无法向您提供本应用程序的服务。',
      },
      {
        title: '10. 联络我们',
        content:
          '如需查阅资料、更正要求或咨询有关我们的资料处理做法，请联络：\n\n' +
          '资料保护主任\n' +
          '电邮：maid-easy-privacy@hfsworld.com',
      },
    ],
    checkboxLabel: '我已阅读、理解并同意以上个人资料收集声明，并同意按照上述方式收集、处理及使用本人的个人资料。',
    buttonLabel: '同意并继续',
    loadingText: '加载中...',
    errorRetry: '重试',
    alreadyConsented: '您已同意最新版本的个人资料收集声明。',
    scrollHint: '请向下滚动阅读完整声明',
    dataNoticeFooter: '您的资料仅用于验证及管理。照片及收据将在 60 天后自动删除。',
  },

  // ═══════════════════════════════════════════════════════════
  // English
  // ═══════════════════════════════════════════════════════════
  en: {
    pageTitle: 'Personal Information Collection Statement',
    subtitle: 'PICS & Data Processing Consent',
    versionLabel: 'Version',
    sections: [
      {
        title: '1. Introduction',
        content:
          'FDH ("the App") is a household management platform designed for employers and foreign domestic helpers in Hong Kong. This Personal Information Collection Statement ("PICS") is issued in compliance with the Personal Data (Privacy) Ordinance (Cap. 486) of Hong Kong ("PDPO"). By using the App, you acknowledge that you have read, understood, and agreed to this PICS.',
      },
      {
        title: '2. Data We Collect',
        content:
          'We collect and process the following categories of personal data:\n\n' +
          'a) Account Information: Email address, nickname, user role (employer/helper), and encrypted password\n' +
          'b) Task Verification Data: Photos (1-3 per task completion), GPS location data (optional, when enabled by user), and notes/remarks\n' +
          'c) Financial Data: Receipt images, itemized expense data, budget information, and settlement records\n' +
          'd) Family Member Data: Names, birthdays, food preferences, food allergies, and medical conditions (employer-entered, with confirmed family consent)\n' +
          'e) Communication Data: Comments and messages between paired employer and helper\n' +
          'f) Technical Data: Device information, IP address, browser type, and usage logs',
      },
      {
        title: '3. Purpose of Data Collection',
        content:
          'Your personal data is collected for the following purposes:\n\n' +
          'a) Account registration, authentication, and session management\n' +
          'b) Enabling task assignment, completion verification, and review between employer and helper\n' +
          'c) Receipt image analysis using artificial intelligence (AI) for expense tracking\n' +
          'd) Money Given / Money Received management and financial settlement between employer and helper\n' +
          'e) Facilitating communication between paired users\n' +
          'f) Compliance with legal and regulatory requirements\n' +
          'g) System security, audit logging, and fraud prevention',
      },
      {
        title: '4. Data Retention and Automatic Deletion',
        content:
          'a) Photos, GPS data, and receipt images: Automatically deleted 60 days after upload\n' +
          'b) AI-processed data: Original images are deleted from memory immediately after AI analysis; only the structured data review results are retained\n' +
          'c) Audit logs: Retained for 3 years as required by applicable regulations\n' +
          'd) Account data: Retained while the account is active',
      },
      {
        title: '5. Artificial Intelligence (AI) Processing',
        content:
          'a) Receipt images are processed using Google Gemini AI to extract structured data (company, shop, date, currency, amounts, items)\n' +
          'b) AI processing is performed in memory only — original images are not permanently stored by the AI service\n' +
          'c) Users must review and can manually override all AI-extracted data before submission\n' +
          'd) AI processing is subject to daily usage limits',
      },
      {
        title: '6. Data Sharing and Disclosure',
        content:
          'a) Your data is shared only with your paired employer or helper within the App\n' +
          'b) Receipt images are temporarily processed by Google Gemini AI for data extraction\n' +
          'c) We do not sell, trade, or share your personal data with unaffiliated third parties\n' +
          'd) We may disclose data when required by law, regulation, or legal process',
      },
      {
        title: '7. Data Security',
        content:
          'a) All data is transmitted using encrypted connections (HTTPS/TLS)\n' +
          'b) Passwords are hashed using industry-standard algorithms\n' +
          'c) Authentication uses JSON Web Tokens (JWT) with httpOnly secure cookies\n' +
          'd) Access to data is restricted to authorized paired users only',
      },
      {
        title: '8. Your Rights',
        content:
          'Under the PDPO, you have the right to:\n\n' +
          'a) Request access to your personal data\n' +
          'b) Request correction of inaccurate personal data\n' +
          'c) Request information about the types of data held and how it is used\n' +
          'd) Withdraw consent (which may result in the inability to use the App)',
      },
      {
        title: '9. Voluntary Provision',
        content:
          'Provision of personal data is voluntary. However, if you do not provide the required information, we may not be able to provide you with the services offered by the App.',
      },
      {
        title: '10. Contact Us',
        content:
          'For data access, correction requests, or inquiries about our data practices, please contact:\n\n' +
          'Data Protection Officer\n' +
          'Email: maid-easy-privacy@hfsworld.com',
      },
    ],
    checkboxLabel:
      'I have read, understood, and agree to the above Personal Information Collection Statement, and I consent to the collection, processing, and use of my personal data as described.',
    buttonLabel: 'Agree and Continue',
    loadingText: 'Loading...',
    errorRetry: 'Retry',
    alreadyConsented: 'You have already consented to the latest version.',
    scrollHint: 'Please scroll down to read the full statement',
    dataNoticeFooter:
      'Your data is used for verification and management only. Photos and receipts are automatically deleted after 60 days.',
  },
};
